// Builds the payload for POST /exception (paysend-web ErrorController).
// Keep in sync with apps/promo/helpers/errorReport.ts
import type { ApiField } from 'apps/auth/helpers/api';
import { getTranslationInfo } from 'apps/auth/helpers/translationInfo';

export const URL_TOKEN_MASK = ':token';

const TOKEN_MIN_LENGTH = 24;
const TOKEN_RE = new RegExp(`^[A-Za-z0-9_-]{${TOKEN_MIN_LENGTH},}$`);
// human-readable slugs (e.g. blog articles) are long too, but must stay readable: "send-money-to-india-in-5-minutes"
const SLUG_RE = /^(?:[a-z]+|\d{1,4})(?:-(?:[a-z]+|\d{1,4}))+$/;

const isTokenLike = (value: string): boolean => TOKEN_RE.test(value) && !SLUG_RE.test(value);

// query and fragment are dropped: they are rarely useful for debugging and are where secrets usually live
export const maskUrl = (url: string): string => {
    try {
        const { origin, pathname } = new URL(url);
        return (
            origin +
            pathname
                .split('/')
                .map((segment) => (isTokenLike(segment) ? URL_TOKEN_MASK : segment))
                .join('/')
        );
    } catch {
        return '';
    }
};

type ErrorReportFields = Record<string, ApiField<string>>;

const field = (name: string, value?: string | null): ApiField<string> => ({ name, value: value ?? undefined });

const safeString = (value: unknown): string => {
    try {
        return String(value);
    } catch {
        return '';
    }
};

export const getErrorReportFields = (error: unknown, componentStack?: string | null): ErrorReportFields => {
    // duck typing instead of instanceof: errors may come from another realm (iframe) or be thrown non-Error values
    const { message, name, stack } = (error ?? {}) as { message?: unknown; name?: unknown; stack?: unknown };
    const translation = getTranslationInfo();

    return {
        // existing fields: format is relied upon by saved searches ("react: ajax exception: JS Error!")
        message: field('message', `JS Error!  in "${maskUrl(document.URL)}".`),
        exception: field('exception', `${safeString(stack)}\n${safeString(componentStack)}`),
        // new fields; "message" name is already taken by the field above
        errorMessage: field('errorMessage', typeof message === 'string' ? message : safeString(error)),
        errorName: field('errorName', typeof name === 'string' ? name : typeof error),
        userAgent: field('userAgent', typeof navigator !== 'undefined' ? navigator.userAgent : null),
        translationService: field('translation.service', translation?.service),
        translationFrom: field('translation.from', translation?.from),
        translationTo: field('translation.to', translation?.to),
    };
};
